import { type hfb } from "@repo/api";
import useSWR from "swr";

import * as fetcher from "../fetcher";
import { mutateWithStartKey } from "../mutateWithStartKey";
import {
  type ConsentType,
  type Customer,
  type CustomerConsent,
  type CustomerConsentPayload,
  getCustomerPayload,
} from "./types";

export const useConsent = (partyId?: string, isRefresh?: boolean) => {
  const { data, isValidating } = useSWR<CustomerConsentPayload>(
    partyId ? `/customers/${partyId}/consents` : null,
    (url: string) => fetcher.opportunity.get<CustomerConsentPayload>(url),
    {
      revalidateOnFocus: Boolean(isRefresh),
      refreshInterval: isRefresh ? 3000 : 0,
    }
  );

  return { data, isValidating };
};

export const triggerConsentMessage = (
  customer: Customer,
  contactType: "EMAIL" | "MOBILE",
  consentTypes: ConsentType[],
  departments?: hfb[],
  opportunityId?: number
) => {
  return fetcher.opportunity
    .post<null, true>(`/customer/consents`, {
      ...getCustomerPayload(customer),
      customerConsents: consentTypes.map((consentType) => ({
        consentType,
        hfb: departments,
        opportunityId,
      })),
      captureConsentMessage: {
        type: contactType,
        contact:
          contactType === "EMAIL" ? customer.email : customer.phoneNumber,
      },
      opportunityId,
    })
    .then((res) => {
      mutateWithStartKey(`/customers/${customer.partyId}/consents`);
      return res;
    });
};

export const createConsents = (
  customer: Customer,
  consents: CustomerConsent[],
  departments?: hfb[],
  opportunityId?: number
) => {
  return fetcher.opportunity
    .post<null, true>(`/customer/consents`, {
      ...getCustomerPayload(customer),
      customerConsents: consents.map((consent) => ({
        ...consent,
        hfb: departments,
        opportunityId,
      })),
      opportunityId,
    })
    .then((res) => {
      mutateWithStartKey(`/customers/${customer.partyId}/consents`);
      return res;
    });
};

export const updateConsents = (
  partyId?: string,
  consents?: CustomerConsent | CustomerConsent[]
) => {
  return fetcher.opportunity
    .update(`/customers/${partyId}/consents`, consents as object)
    .then((res) => {
      mutateWithStartKey(`/customers/${partyId}/consents`);
      return res;
    });
};
